import { FC, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import './FaultInjectionHeader.css';
import '../../../CommonComponents/faultInjectionListView/FaultInjection.css'
import useViewer from '../../hooks/useViewer';
import { get_formated_time } from '../../utils';
import useUrlParams from '../../hooks/useUrlParams';
import CustomToolTip from '../../../CommonComponents/CustomToolTip';
import useContacts from '../../hooks/useContacts';
import { CheckIcon, DatabaseIcon, FailedFaultSvg, FaultInjectionButton, FaultInjectionButtonActive, NextFaultInject, StopFaultSvg } from './Svgs';
import useSocket from '../../hooks/useSocket';
import DynamicForm from '../../../CommonComponents/dynamic_form/DynamicForm';
import useScenario from '../../hooks/useScenario';
import { processFaultData, getFaultDataFromList } from '../../../CommonComponents/faultInjectionListView/utils/faultMappingUtils';
import { replaceUnderscoresWithSpaces } from '../../../../utils/commonFunctions';
import { FaultSchedulingChips } from '../../../CommonComponents/faultInjectionListView/components/FaultSchedulingChips';
import { isOperateMode } from '../../hooks/utils';


const tabs = [
    { id: 'defined', label: 'Defined' },
    { id: 'probable', label: 'Probable' }
];
const FaultInjectionHeader: FC = () => {
    const [showMainModal, setShowMainModal] = useState(false);
    const [showInject, setShowInject] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const { current_simulation_time } = useViewer();
    const { dashboard, sat_grp_id, sat_id, scenario_id } = useUrlParams();
    const { scenario } = useScenario()
    const operateMode = isOperateMode(dashboard, scenario)
    const { fetchInjectedFaultList, injectedFaultList, supportedSystemFaults, getSupportedSystemFaults, postInject, cancelFaultInjected, stopFaultInjected } = useContacts();
    const { socket } = useSocket()
    const current_time = useMemo(() => current_simulation_time / 1000, [current_simulation_time]);
    const [activeTab, setActiveTab] = useState<string>(tabs?.[0]?.id || 'defined');
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const [openFaultId, setOpenFaultId] = useState<string | number | null>(null);
    const { simulation_time } = useScenario();


    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
    };
    const handleDropdownToggle = (faultId: string) => {
        setOpenDropdownId(openDropdownId === faultId ? null : faultId);
    };

    const toggleDetails = (faultId: string | number) => {
        setOpenFaultId((prev: string | number | null) => (prev === faultId ? null : faultId));
    };


    // Memo: normalize groups list from fault schedule response
    const groups = useMemo(() => (Array.isArray(injectedFaultList) ? injectedFaultList : []), [injectedFaultList])

    // Memo: filter satellites for the selected satellite group
    const satellitesInGroup = useMemo(() => (
        groups?.flatMap((group) => (group?.satelliteGroupId === sat_grp_id ? (group?.satellites ?? []) : []))
    ), [groups, sat_grp_id])

    // Memo: currently selected satellite's schedule entry
    const selectedSatEntry = useMemo(() => (
        satellitesInGroup?.find((satellite) => satellite?.satId === sat_id)
    ), [satellitesInGroup, sat_id])

    // Memo: derive faultScheduleId from the selected satellite entry
    const faultScheduleId = useMemo(() => selectedSatEntry?.faultScheduleId, [selectedSatEntry]);

    useEffect(() => {
        if (fetchInjectedFaultList) {
            fetchInjectedFaultList();
        }
        if (getSupportedSystemFaults) {
            getSupportedSystemFaults();
        }
        setShowMainModal(false)
    }, [sat_grp_id, sat_id]);

    const faults = useMemo(() => {
        const faultData = getFaultDataFromList(selectedSatEntry);
        return processFaultData(faultData, dashboard, current_time);
    }, [selectedSatEntry, current_time, dashboard]);


    // Separate faults into SCHEDULED and NOT_SCHEDULED
    const scheduledFaults = faults?.filter(fault => fault?.status === "SCHEDULED");


    const handleOpen = () => {
        setShowMainModal(prev => !prev);
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowInject(false);
            setIsClosing(false);
        }, 400); // Match with animation duration
    };

    const handleCancelFault = (faultScheduleId: string, faultId: string | number) => {
        cancelFaultInjected(faultScheduleId, faultId as string);
    };

    useEffect(() => {
        if (!operateMode && socket) {
            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.eventType === 'FAULT_UPDATE') {
                        fetchInjectedFaultList();
                    }

                } catch (error) {
                    // Silently handle parsing errors to prevent console noise
                    // Error is already handled by try-catch, no user action needed
                }
            };
        }
    }, [socket, scenario_id])

    return (
        <>
            <CustomToolTip title="Fault Injection" placement="top">
                <button className={`fault-injection-main-button ${showMainModal && 'active'}`} onClick={handleOpen}>
                    {showMainModal ? <FaultInjectionButtonActive /> : <FaultInjectionButton />}
                </button>
            </CustomToolTip>
            {showMainModal && (
                <div className="fault-modal-wrapper">
                    <div className="fault-container" onClick={(e) => e.stopPropagation()}>
                        <div className="fault-header">
                            <div className="fault-title"> <FaultInjectionButton />&nbsp;&nbsp;Fault Plan</div>
                            <div className='d-flex gap-3'>
                                {dashboard === 'simulation' && <div className="add-fault-button" title='Inject Fault' onClick={() => setShowInject(true)}>+</div>}
                                {!showInject && <div className="add-fault-button fs-14 m-auto" title='Close' onClick={handleOpen}>
                                    &#10005;
                                </div>}
                            </div>
                        </div>
                        <div className={`fi-tabs-bar d-none`}>
                            <div className="fi-tabs">
                                {tabs?.map(tab => {
                                    const isActive = activeTab === tab.id;
                                    return (
                                        <div key={tab.id} className={`fi-tab ${isActive ? 'fi-tab-active' : ''}`} onClick={() => handleTabChange(tab.id)}>
                                            {tab.label}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="fi-counter">
                                <span>{scheduledFaults?.length}</span>
                                <span className="fi-count-separator">/</span>
                                <span className="fi-count-total">{faults?.length}</span>
                            </div>
                        </div>
                        <div className="fault-list">
                            <div className={`fi-list`}>
                                {faults && faults?.length > 0 ? (
                                    faults?.map((fault) => {
                                        return (
                                            <div
                                                key={fault.faultId}
                                                className={`fi-item ${fault?.status === 'EXECUTING' ? 'executing' : ''} ${fault?.status === 'CANCELLED' ? 'cancelled' : ''}`}
                                                onClick={() => { toggleDetails(fault.faultId); setOpenDropdownId(null) }}
                                            >
                                                <div className="fi-item-header">
                                                    <div className={`fi-component ${fault?.status === 'CANCELLED' ? 'strike' : ''}`}>
                                                        <div className="fi-component-icon">
                                                            <DatabaseIcon />
                                                        </div>
                                                        <div>&nbsp;{fault.component}</div>
                                                    </div>

                                                    <div className="fi-time-status">
                                                        <div className={`fi-time ${fault?.status === 'CANCELLED' ? 'strike' : ''}`}>
                                                            {get_formated_time(fault?.time)}
                                                        </div>
                                                        {fault?.status !== 'CANCELLED' && (
                                                            (fault?.status === 'COMPLETED') ? (
                                                                <div className="fi-status">
                                                                    <CheckIcon />
                                                                </div>
                                                            ) : fault?.status === 'FAILED' ? (
                                                                <div className="fi-status">
                                                                    <FailedFaultSvg />
                                                                </div>
                                                            ) : (
                                                                (fault?.status === 'SCHEDULED' || (current_time && current_time < fault?.time)) && (
                                                                    <div className='fi-status'>
                                                                        <NextFaultInject />
                                                                    </div>
                                                                )
                                                            )
                                                        )}
                                                    </div>


                                                </div>

                                                {/* Row Content */}
                                                <div className="fi-row">
                                                    <div
                                                        className={`fi-message ${fault?.status === "CANCELLED" ? "strike" : ""
                                                            }`}
                                                    >
                                                        {fault?.faultName}
                                                    </div>

                                                    {fault?.status === "CANCELLED" ? (
                                                        <div className="no-strike status-chip status-chip--cancelled">
                                                            Cancelled
                                                        </div>
                                                    ) : fault?.status === "ABORTED" || fault?.status === "COMPLETED" ? (
                                                        <div className="no-strike status-chip status-chip--restored">
                                                            System Restored
                                                        </div>
                                                    ) : fault?.status === "FAILED" ? null : (
                                                        dashboard === "simulation" &&
                                                        (fault?.status === "SCHEDULED" ? (
                                                            <div
                                                                className="fi-dropdown-trigger-container"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <button
                                                                    className="fi-dropdown-trigger"
                                                                    aria-haspopup="menu"
                                                                    aria-expanded={openDropdownId === fault.faultId}
                                                                    aria-controls={`fi-menu-${fault.faultId}`}
                                                                    onClick={() => handleDropdownToggle(fault.faultId)}
                                                                    title="Actions"
                                                                >
                                                                    ⋮
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            fault?.status === "EXECUTING" && (
                                                                <div
                                                                    className="stop-fault-icon-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        stopFaultInjected(faultScheduleId as string, fault.faultId as string);
                                                                    }}
                                                                    title="Stop Fault"
                                                                >
                                                                    <StopFaultSvg />
                                                                </div>

                                                            )
                                                        ))
                                                    )}

                                                    {openDropdownId === fault.faultId && (
                                                        <div
                                                            id={`fi-menu-${fault.faultId}`}
                                                            className="fi-dropdown-menu"
                                                            role="menu"
                                                            aria-label="Fault actions"
                                                            onKeyDown={(e) => { if (e.key === 'Escape') setOpenDropdownId(null); }}
                                                        >
                                                            <button
                                                                className="fi-dropdown-item"
                                                                role="menuitem"
                                                                autoFocus
                                                                onClick={() => handleCancelFault(faultScheduleId as string, fault.faultId as string)}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>


                                                {/* Expandable Box */}
                                                {openFaultId === fault.faultId && (
                                                    <div className="fi-expanded-box">
                                                        {/* Header */}
                                                        <div className="fi-expanded-header">
                                                            <div className="fi-section-title">
                                                                Scheduling requirement
                                                            </div>
                                                            <div className="d-flex align-items-center gap-1 fi-scheduling-chips justify-content-end">
                                                                <FaultSchedulingChips fault={fault} />
                                                            </div>
                                                        </div>
                                                        {/*Body*/}
                                                        {!isEmpty(fault?.faultDetails) && (
                                                            <div className="fi-config-wrapper">
                                                                <div className="fi-config-title">Configuration</div>
                                                                {Object?.entries(fault?.faultDetails || {})?.map(([key, value]) => (
                                                                    <div className="fi-config-content" key={`${key}-${String(value)}`}>
                                                                        <div className="fi-config-key-container">{replaceUnderscoresWithSpaces(key)}</div>
                                                                        <div className="fi-config-value-container">
                                                                            {value as string}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    false || (
                                        <div className="fi-no-faults">No Faults</div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>

                    {showInject && dashboard === 'simulation' && (
                        <div
                            className={`fault-inject-wrapper ${isClosing ? 'slide-out' : 'slide-in-from-behind'}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="fault-header">
                                <div className="fault-title">Inject Fault</div>
                                <div className="add-inject-button fs-14" title='Close' onClick={handleClose}>
                                    &#10005;
                                </div>
                            </div>

                            <div className="modal-body mt-0 w-100 overflow-auto">
                                <DynamicForm
                                    supportedSystemFaults={supportedSystemFaults}
                                    faultScheduleId={faultScheduleId}
                                    postInject={postInject as any}
                                    simulation_time={simulation_time}
                                    current_simulation_time={current_simulation_time}
                                    dashboard={"simulation"}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};



export default FaultInjectionHeader;

